import { useCallback, useEffect, useRef, useState } from 'react';
import { T } from '../../components/text/TextEdit';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  BarChart3, RefreshCw, Check, X, Quote, TrendingUp,
  Search, Clock, ThumbsUp as ThumbsUpIcon, Activity, Megaphone,
  Swords, Rocket, ShieldAlert, Timer, Flag, FlaskConical, History, Building2, AlertTriangle, CheckCircle2,
} from 'lucide-react';
import { api } from '../../lib/api';
import { CreditBanner, isCreditError } from './CreditBanner';
import { Button } from '../ui/button';
import { SectionCard, Field, ProvenanceLabel, Disclaimer, Skeleton, InfoCard, GenerationProgress, VersionSwitcher } from './ui';
import { SourceBadge, ScoreRadar, SubScoreBars, RiskMatrix, DemandTrend } from './charts';
import { useVersions } from './useVersions';
import { parsePartialJson } from '../../lib/partialJson';
import { ServedModelBadge } from './ServedModelBadge';

// The 4 heavy dimension boxes (index 0..3), mirrors backend MARKET_DIMENSIONS order. Used to
// detect a resumable partial run (a stored skeleton with one or more dimensions still missing).
const DIMENSION_KEYS = ['competitive_landscape', 'market_sizing_stage', 'key_risk', 'risk_matrix'];

// Retry pacing for a dimension call. Delays sit between attempts 1→2 and 2→3.
const RETRY_BACKOFF_MS = [1500, 4000];
// Hard ceiling on how long one dimension may occupy the stage before we stop and let the user
// resume. The server aborts a provider call at 75s, so three attempts could otherwise run to
// nearly four minutes of blank screen for a single box.
const MAX_DIMENSION_MS = 150_000;
// Runaway guard only. The real count comes from the server's groups_total so the client cannot
// drift out of step with MARKET_DETAIL_GROUPS.
const MAX_DETAIL_GROUPS = 12;

// Nonces from ?start= that have already fired, held at MODULE level on purpose: React 18
// StrictMode mounts every component twice in dev, and a per-instance ref is reborn on the
// second mount before setSearchParams has flushed — which is how one dashboard click started
// TWO concurrent analyses on 2026-08-03 (two dimension appends 5s apart, both billed). The
// module survives the remount, so a nonce can fire exactly once per page load, full stop.
const consumedStarts = new Set();

// Projects with an auto-resume currently in flight. Same double-mount hazard as the nonces,
// same module-level cure — but a Set that CLEARS on completion, so returning to a page whose
// run is still unfinished can auto-resume again.
const resumesInFlight = new Set();
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

function Bullets({ items, testId }) {
  if (!Array.isArray(items) || items.length === 0) return <span className="text-muted-foreground text-sm">—</span>;
  return <ul className="list-disc ps-5 space-y-1 text-sm">{items.map((x, i) => <li key={`${x}-${i}`}>{x}</li>)}</ul>;
}

function GoStop({ go = [], stop = [] }) {
  const { t } = useTranslation();
  return (
    <div className="grid sm:grid-cols-2 gap-4">
      <div className="p-5 border border-success-border bg-success-soft rounded-md" data-testid="go-signals">
        <p className="text-xs tracking-[0.18em] uppercase font-bold text-success mb-2"><T k="market.goSignals" /></p>
        <ul className="space-y-1.5 text-sm">{(go || []).map((s, i) => <li key={`${s}-${i}`} className="flex gap-2"><Check className="h-4 w-4 text-success shrink-0 mt-0.5" />{s}</li>)}</ul>
      </div>
      <div className="p-5 border border-destructive-border bg-destructive-soft rounded-md" data-testid="stop-signals">
        <p className="text-xs tracking-[0.18em] uppercase font-bold text-destructive mb-2"><T k="market.stopSignals" /></p>
        <ul className="space-y-1.5 text-sm">{(stop || []).map((s, i) => <li key={`${s}-${i}`} className="flex gap-2"><X className="h-4 w-4 text-destructive shrink-0 mt-0.5" />{s}</li>)}</ul>
      </div>
    </div>
  );
}

function GroupHeading({ icon: Icon, children }) {
  return (
    <h3 className="flex items-center gap-2 text-lg font-semibold text-foreground px-1 pt-2">
      {Icon && <Icon className="h-5 w-5 text-muted-foreground shrink-0" />}
      {children}
    </h3>
  );
}

function MarketResult({ m }) {
  const { t } = useTranslation();
  const ev = m.market_evidence || {};
  const lds = m.live_demand_signals || {};
  const comp = m.competitive_landscape || {};
  const sizing = m.market_sizing_stage || {};
  const risk = m.key_risk || {};
  const prec = m.precedents || {};
  const breakdown = Array.isArray(m.score_breakdown) ? m.score_breakdown : [];
  const src = (x) => (x && x.source ? <SourceBadge source={x.source} /> : null);

  return (
    <div className="flex flex-col gap-3" data-testid="market-output">
      {/* Market score — large number + breakdown */}
      <section className="border border-border bg-card rounded-xl shadow-sm p-6 md:p-8 flex flex-col gap-6" data-testid="market-score-card">
        <div className="flex items-start justify-between gap-3">
          <h3 className="flex items-center gap-2 text-lg font-semibold text-foreground"><BarChart3 className="h-5 w-5 text-muted-foreground" /><T k="market.scoreTitle" /></h3>
          <ProvenanceLabel value={m.score_label} />
        </div>
        <div className="flex flex-col sm:flex-row sm:items-center gap-6">
          <div className="flex items-end gap-2 shrink-0">
            <span className="text-6xl font-bold tracking-tighter text-foreground font-data leading-none" data-testid="market-score">{m.market_score ?? '—'}</span>
            <span className="text-muted-foreground text-sm mb-1.5">/ 100</span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs tracking-[0.18em] uppercase font-bold text-muted-foreground mb-1"><T k="market.whatThisMeans" /></p>
            <p className="text-foreground leading-relaxed" data-testid="market-what-this-means">{m.what_this_means || '—'}</p>
          </div>
        </div>
        {m.no_corpus_note && <Disclaimer testId="market-no-corpus-disclaimer">{m.no_corpus_note}</Disclaimer>}
        {breakdown.length > 0 && (
          <div className="grid lg:grid-cols-2 gap-6 border-t border-border pt-6" data-testid="market-breakdown-card">
            <div>
              <p className="text-xs tracking-[0.18em] uppercase font-bold text-muted-foreground mb-2"><T k="market.breakdownTitle" /></p>
              <ScoreRadar data={breakdown} />
            </div>
            <div>
              <p className="text-xs tracking-[0.18em] uppercase font-bold text-muted-foreground mb-2"><T k="market.breakdownTitle" /></p>
              <SubScoreBars data={breakdown} />
            </div>
          </div>
        )}
      </section>

      <GoStop go={m.go_signals} stop={m.stop_signals} />

      {/* Market evidence — one card per signal */}
      <GroupHeading icon={Activity}><T k="market.evidenceTitle" /></GroupHeading>
      <div className="grid sm:grid-cols-2 gap-3">
        <InfoCard icon={Search} title={t('market.searchDemand')} action={src(ev.search_demand)} testId="evidence-search-demand">{ev.search_demand?.text}</InfoCard>
        <InfoCard icon={Clock} title={t('market.marketTiming')} action={src(ev.market_timing)} testId="evidence-market-timing">{ev.market_timing?.text}</InfoCard>
        <InfoCard icon={ThumbsUpIcon} title={t('market.socialProof')} action={src(ev.social_proof)} testId="evidence-social-proof">{ev.social_proof?.text}</InfoCard>
        <InfoCard icon={TrendingUp} title={t('market.growingPain')} action={src(ev.growing_pain)} testId="evidence-growing-pain">{ev.growing_pain?.text}</InfoCard>
        <InfoCard icon={Megaphone} title={t('market.socialMedia')} action={src(ev.social_media)} testId="evidence-social-media" className="sm:col-span-2">{ev.social_media?.text}</InfoCard>
      </div>

      {/* Competitive landscape */}
      <InfoCard icon={Swords} title={t('market.competitive')} action={src(comp)} testId="competitive-landscape">
        <p className="mb-2">{comp.text}</p>
        {Array.isArray(comp.competitors) && comp.competitors.length > 0 && (
          <ul className="space-y-1.5">{comp.competitors.map((c, i) => <li key={`${c.name || 'comp'}-${i}`} className="text-sm"><span className="font-semibold">{c.name}</span> — {c.note}</li>)}</ul>
        )}
      </InfoCard>

      {/* Go-to-market */}
      <InfoCard icon={Rocket} title={t('market.gtmTitle')} action={src(m.gtm_snapshot)} testId="gtm-snapshot">
        <p>{m.gtm_snapshot?.text || '—'}</p>
        {(Array.isArray(m.whats_working) || Array.isArray(m.watch_out_for)) && (
          <div className="grid sm:grid-cols-2 gap-5 mt-3 border-t border-border pt-3">
            <Field label={<T k="market.whatsWorking" />}><Bullets items={m.whats_working} testId="whats-working" /></Field>
            <Field label={<T k="market.watchOutFor" />}><Bullets items={m.watch_out_for} testId="watch-out-for" /></Field>
          </div>
        )}
      </InfoCard>

      {/* Key risk + mitigation */}
      <InfoCard icon={ShieldAlert} title={t('market.keyRisk')} action={src(risk)} testId="key-risk">
        <p className="mb-2"><span className="font-semibold">{t('market.risk')}:</span> {risk.risk}</p>
        <p className="mb-4"><span className="font-semibold">{t('market.mitigation')}:</span> {risk.mitigation}</p>
        {Array.isArray(m.risk_matrix) && m.risk_matrix.length > 0 && (
          <>
            <p className="text-xs tracking-[0.18em] uppercase font-bold text-muted-foreground mb-2"><T k="market.riskMatrix" /></p>
            <RiskMatrix items={m.risk_matrix} />
          </>
        )}
      </InfoCard>

      {/* Time to first customer */}
      <InfoCard icon={Timer} title={t('market.timeToCustomer')} action={src(m.time_to_first_customer)} testId="time-to-customer">{m.time_to_first_customer?.text}</InfoCard>

      {/* Launch strategy */}
      <InfoCard icon={Flag} title={t('market.launchStrategy')} action={src(m.launch_strategy)} testId="launch-strategy">{m.launch_strategy?.text}</InfoCard>

      {/* Market sizing & stage */}
      <InfoCard icon={Building2} title={t('market.sizingStage')} action={src(sizing)} testId="market-sizing-stage">
        <p className="mb-1">{sizing.sizing}</p>
        {sizing.stage && <p className="text-xs font-data text-muted-foreground uppercase tracking-wide">{t('market.stage')}: {sizing.stage}</p>}
      </InfoCard>

      {/* Live demand signals */}
      {((lds.social_quotes || []).length > 0 || (lds.search_trends || []).length > 0) && (
        <InfoCard icon={Quote} title={t('market.liveSignalsTitle')} testId="market-signals-card">
          <p className="text-sm text-muted-foreground mb-3"><T k="market.liveSignalsHint" /></p>
          <div className="grid sm:grid-cols-2 gap-4">
            {(lds.social_quotes || []).map((q, i) => (
              <div key={`${q.quote || 'quote'}-${i}`} className="p-4 border border-border rounded-md bg-muted flex flex-col gap-2" data-testid={`social-quote-${i}`}>
                <div className="flex items-center justify-between"><Quote className="h-4 w-4 text-muted-foreground" /><SourceBadge source={q.source} /></div>
                <p className="text-sm text-foreground italic">“{q.quote}”</p>
                {q.author && <p className="text-xs text-muted-foreground font-data">— {q.author}</p>}
              </div>
            ))}
            {(lds.search_trends || []).map((tr, i) => (
              <div key={`${tr.term || 'trend'}-${i}`} className="p-4 border border-border rounded-md bg-muted flex flex-col gap-2" data-testid={`search-trend-${i}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-medium text-foreground flex items-center gap-1.5"><TrendingUp className="h-4 w-4 text-muted-foreground" />{tr.term}</span>
                  <SourceBadge source={tr.source} />
                </div>
                {Array.isArray(tr.points) && tr.points.length > 0
                  ? <DemandTrend data={tr.points.map((p, idx) => ({ period: String(idx + 1), value: p }))} testId={`search-trend-line-${i}`} />
                  : <span className="text-xs text-muted-foreground font-data">{tr.direction}</span>}
              </div>
            ))}
          </div>
        </InfoCard>
      )}

      {/* Validation */}
      <InfoCard icon={FlaskConical} title={t('market.validationTitle')} testId="market-validation-card">
        <div className="grid sm:grid-cols-2 gap-5">
          <Field label={<T k="market.experiments" />}><Bullets items={m.validation_experiments} testId="validation-experiments" /></Field>
          <Field label={<T k="market.interviewQuestions" />}><Bullets items={m.key_interview_questions} testId="interview-questions" /></Field>
        </div>
      </InfoCard>

      {/* Precedents */}
      <InfoCard icon={History} title={t('market.precedents')} action={src(prec)} testId="precedents">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <p className="text-xs font-bold text-success mb-1"><T k="market.whoTried" /></p>
            <ul className="space-y-1 text-sm">{(prec.who_tried || []).map((p, i) => <li key={`${p.name || 'tried'}-${i}`}><span className="font-semibold">{p.name}</span> — {p.outcome}</li>)}</ul>
          </div>
          <div>
            <p className="text-xs font-bold text-destructive mb-1"><T k="market.whoFailed" /></p>
            <ul className="space-y-1 text-sm">{(prec.who_failed || []).map((p, i) => <li key={`${p.name || 'failed'}-${i}`}><span className="font-semibold">{p.name}</span> — {p.reason}</li>)}</ul>
          </div>
        </div>
      </InfoCard>

      {/* Demand trajectory */}
      {Array.isArray(m.demand_trajectory) && m.demand_trajectory.length > 0 && (
        <InfoCard icon={TrendingUp} title={t('market.trajectoryTitle')} testId="market-trajectory-card">
          <DemandTrend data={m.demand_trajectory} testId="demand-trajectory" />
        </InfoCard>
      )}
    </div>
  );
}

export default function MarketStage({ projectId, hasIdea, onChanged, servedModels }) {
  const { t } = useTranslation();
  const [m, setM] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [phase, setPhase] = useState(null); // 'skeleton' | 'dimensions' | 'detail' | null
  const [streamingMarket, setStreamingMarket] = useState(null);
  const [interrupted, setInterrupted] = useState(false); // stream ended without a `done`/payload
  const [resumable, setResumable] = useState(false);
  const [creditBlocked, setCreditBlocked] = useState(false);
  const [creditDone, setCreditDone] = useState(0);   // parts already generated AND paid
  const [creditWaiting, setCreditWaiting] = useState(0); // parts still owed
  const [armed, setArmed] = useState(false); // arrived via the dashboard's analyze click // a stored skeleton has unfilled dimensions
  const vh = useVersions(projectId, 'market_analysis');
  // ?start=<nonce> is the market dashboard's Analyze/Resume button carrying its intent through
  // the navigation, so landing here doesn't demand the same click a second time. Each nonce is
  // consumed exactly once — a reload after consumption must not re-run and re-charge.
  const [searchParams, setSearchParams] = useSearchParams();
  const startNonce = searchParams.get('start');
  // Model picker for the Analyze / Run-fresh buttons (Ali, 2026-08-03) — the same keyless-aware
  // list the bottom Regenerate control uses; empty selection means "server default". Feeds the
  // modelOverride parameter run() already threads through all three passes.
  const [models, setModels] = useState([]);
  const [modelsLoading, setModelsLoading] = useState(true);
  const [selModel, setSelModel] = useState('');
  useEffect(() => {
    api.modelKeyAvailability()
      .then((r) => setModels(r?.models || []))
      .catch(() => setModels([]))
      .finally(() => setModelsLoading(false));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.out('get_market_analysis', { project_id: projectId });
      setM(r.market_analysis);
      // Detect an interrupted run: a stored skeleton whose dimensions aren't all filled.
      const prog = await api.getMarketProgress(projectId);
      const filled = prog?.filled || {};
      const skeletonReady = prog?.skeleton && Object.keys(prog.skeleton).length > 0;
      const missing = DIMENSION_KEYS.some((k) => filled[k] === undefined || filled[k] === null);
      setResumable(Boolean(skeletonReady && missing));
    } catch (e) { toast.error(e.message); } finally { setLoading(false); }
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  // Shared tail: fill the 4 dimension boxes then stream the detail pass. Already-completed
  // dimensions return server-cached (no re-charge), so both a fresh run and a resume loop [0..3].
  const finishAnalysis = async (skeleton, initialFilled, regenerate, modelOverride) => {
    setPhase('dimensions');
    let filled = initialFilled || {};
    let workM = { ...skeleton, ...filled };
    setM(workM);
    for (let idx = 0; idx < DIMENSION_KEYS.length; idx++) {
      const startedAt = Date.now();
      // Up to 3 attempts per dimension — absorbs transient provider/cold-start hiccups.
      let rd = null;
      let lastErr = null;
      for (let attempt = 0; attempt < 3 && !rd; attempt++) {
        try {
          rd = await api.marketDimension({ project_id: projectId, dimension_index: idx, skeleton, filled, model_id: modelOverride || undefined });
        } catch (eDim) {
          lastErr = eDim;
          // Permanent failures (bad request / no credits / not entitled / missing / no key) can
          // never succeed on a retry. Stop now instead of burning two more full generations.
          if ([400, 402, 403, 404, 409].includes(eDim?.status)) break;
          // Back off before retrying. Immediate re-fires were actively harmful against a provider
          // that answers "scheduler queue full" — three rejections inside a second neither help
          // nor give the queue time to drain. Also cap total elapsed: with a 75s server timeout,
          // three blind attempts could hold this stage for nearly four minutes with no output.
          if (attempt < 2) {
            if (Date.now() - startedAt > MAX_DIMENSION_MS) break;
            await sleep(RETRY_BACKOFF_MS[attempt]);
          }
        }
      }
      if (!rd) {
        // Do NOT swallow this. Before, three failed attempts dropped silently into the resume
        // state: the stage just stalled with no message and no console line, which is why a
        // truncated dimension looked like "some sections just didn't generate".
        console.error(`market dimension '${DIMENSION_KEYS[idx]}' failed:`, lastErr);
        if (isCreditError(lastErr)) setCreditBlocked(true);
        else toast.error(`${DIMENSION_KEYS[idx]} — ${lastErr?.message || t('market.detailFailed')}`);
        setStreamingMarket(null); setInterrupted(true); setResumable(true);
        return;
      }
      filled = rd.filled || filled;
      workM = rd.done && rd.market_analysis ? rd.market_analysis : { ...skeleton, ...filled };
      setM(workM);
    }
    setResumable(false);
    onChanged?.();

    // PASS 3 (DETAIL) — SPLIT INTO GROUPS, looped exactly like the dimensions above.
    //
    // This used to be one streamed call producing all 11 sections. Measured 2026-07-30: it
    // truncated at a 3200-token budget, still truncated at 6000, and only completed on the
    // 8000 repair — which asks for a shorter answer, so the delivered depth fell below the
    // prompt's own contract. Same failure the core had; same fix.
    //
    // groups_total comes from the server rather than a constant here, so the two can never
    // drift apart. Already-completed groups return server-cached with no re-charge, so a
    // resume walks the same loop as a fresh run.
    setPhase('detail');
    let groupsTotal = null;
    let detailOut = null;
    let deepM = workM;
    for (let gi = 0; groupsTotal === null || gi < groupsTotal; gi++) {
      if (gi > MAX_DETAIL_GROUPS) break; // guard against a bad groups_total looping forever
      const startedAt = Date.now();
      let rg = null;
      let lastErr = null;
      for (let attempt = 0; attempt < 3 && !rg; attempt++) {
        try {
          rg = await api.marketDetailGroup({ project_id: projectId, group_index: gi, model_id: modelOverride || undefined });
        } catch (eD) {
          lastErr = eD;
          // Permanent failures can never succeed on a retry — stop instead of burning two more.
          if ([400, 402, 403, 404, 409].includes(eD?.status)) break;
          if (attempt < 2) {
            if (Date.now() - startedAt > MAX_DIMENSION_MS) break;
            await sleep(RETRY_BACKOFF_MS[attempt]);
          }
        }
      }
      if (!rg) {
        // The core is already persisted; only this group's deep boxes are missing. Name which
        // one and why — a silent stall is what made truncation look like "some sections just
        // didn't generate".
        console.error(`market detail group ${gi} failed:`, lastErr);
        setStreamingMarket(null);
        await vh.refresh();
        if (isCreditError(lastErr)) {
          const doneCount = DIMENSION_KEYS.filter((k) => filled[k] != null).length;
          setCreditDone(doneCount);
          setCreditWaiting(DIMENSION_KEYS.length - doneCount);
          setCreditBlocked(true); setResumable(true); setInterrupted(true);
        }
        else toast.warning(`${t('market.detailFailed')} — ${lastErr?.message || ''}`);
        return;
      }
      groupsTotal = rg.groups_total ?? groupsTotal;
      if (rg.group) { deepM = { ...deepM, ...rg.group }; setStreamingMarket(deepM); }
      if (rg.done && rg.market_analysis) detailOut = rg;
    }

    if (!detailOut) {
      console.error('market detail finished every group but no version was produced');
      setStreamingMarket(null);
      await vh.refresh();
      toast.warning(t('market.detailFailed'));
      return;
    }
    setM(detailOut.market_analysis);
    setStreamingMarket(null);
    onChanged?.();
    await vh.refresh();
    toast.success(regenerate ? t('market.reanalyzed', { version: detailOut.version_no }) : t('market.ready'));
  };

  const run = async (regenerate, modelOverride) => {
    setCreditBlocked(false);
    setBusy(true);
    setStreamingMarket(null);
    setInterrupted(false);
    try {
      // PASS 1 (SKELETON) — streamed: score + signals paint as they arrive.
      setPhase('skeleton');
      const r1 = await api.streamMarket(
        { project_id: projectId, regenerate, model_id: modelOverride || undefined },
        { onDelta: (p) => { const o = parsePartialJson(p); if (o && typeof o === 'object') setStreamingMarket(o); } },
      );
      // GUARD: a stream that ends with no `done` event resolves to null → never read .skeleton on null.
      if (!r1 || !r1.skeleton) { setStreamingMarket(null); setInterrupted(true); return; }
      const skeleton = r1.skeleton;
      setM(skeleton);
      setStreamingMarket(null);
      onChanged?.();
      // PASS 2 + 3: the skeleton pass reset progress server-side, so start dimensions fresh.
      await finishAnalysis(skeleton, {}, regenerate, modelOverride);
    } catch (e) {
      if (isCreditError(e)) { setCreditBlocked(true); setResumable(true); setInterrupted(true); }
      else toast.error(e.message);
    } finally {
      setBusy(false);
      setPhase(null);
      setStreamingMarket(null);
    }
  };

  // RESUME: reload/disconnect recovery — reuse the stored skeleton + completed dimensions and loop
  // only what's missing. NO skeleton pass ⇒ the skeleton and finished dimensions are never re-charged.
  const resume = async () => {
    setCreditBlocked(false); setCreditDone(0); setCreditWaiting(0);
    setBusy(true);
    setStreamingMarket(null);
    setInterrupted(false);
    try {
      const prog = await api.getMarketProgress(projectId);
      if (!prog?.skeleton || Object.keys(prog.skeleton).length === 0) { await run(false); return; }
      await finishAnalysis(prog.skeleton, prog.filled || {}, false);
    } catch (e) {
      if (isCreditError(e)) { setCreditBlocked(true); setResumable(true); setInterrupted(true); }
      else toast.error(e.message);
    } finally {
      setBusy(false);
      setPhase(null);
      setStreamingMarket(null);
    }
  };

  // Latest run/resume, readable from the effect below without listing two every-render
  // functions as dependencies (wrapping them in useCallback would cascade through
  // finishAnalysis and the whole retry machinery for no behavioural gain).
  const actionsRef = useRef(null);
  actionsRef.current = { run, resume };

  // Fire the carried intent once everything it needs is known: this stage's own load has
  // resolved (so `resumable` and `m` are real, not initial state) and the project has an idea.
  // A finished analysis ignores the flag — the URL is user input, and a stale or hand-typed
  // ?start=1 must not burn a generation on top of a completed run.
  useEffect(() => {
    if (loading || busy || !hasIdea || !startNonce || consumedStarts.has(startNonce)) return;
    consumedStarts.add(startNonce);
    setSearchParams({}, { replace: true });
    // NO auto-start (Ali, 2026-08-16): a dashboard click used to jump straight into a paid
    // run, so the model picker was unreachable — it only appeared once the run had begun,
    // disabled. Arriving with ?start now simply flags the stage as "ready to run"; the user
    // picks the model and presses the button. The resumable case still belongs to the
    // auto-resume effect below (finishing a paid run is the standing intent; STARTING one
    // is not).
    if (!resumable && !m) setArmed(true);
  }, [loading, busy, hasIdea, resumable, m, startNonce, setSearchParams]);

  // AUTO-RESUME (Ali, 2026-08-03). An interrupted run used to greet the returning user as a
  // half-empty analysis plus two competing buttons, and picking the wrong one appended an
  // orphan hollow version. Finishing what was started is the user's standing intent — the run
  // was deliberately begun and partially paid for — so arriving at unfinished progress resumes
  // it: completed dimensions and detail groups return server-cached (never re-charged), and the
  // resume COMPLETES the same version rather than appending one. Only on arrival: an in-session
  // failure sets `interrupted` and stays manual, so a dead provider cannot cause a retry loop.
  useEffect(() => {
    if (loading || busy || !hasIdea || !resumable || interrupted || creditBlocked) return;
    if (resumesInFlight.has(projectId)) return;
    resumesInFlight.add(projectId);
    actionsRef.current.resume().finally(() => resumesInFlight.delete(projectId));
  }, [loading, busy, hasIdea, resumable, interrupted, creditBlocked, projectId]);

  const primaryLabel = () => {
    if (phase === 'skeleton' || phase === 'dimensions') return t('market.analyzing');
    if (phase === 'detail') return t('market.loadingDeeper');
    return m ? t('market.runFresh') : t('market.analyze');
  };

  const shownMarket = streamingMarket || vh.viewPayload || m;
  let body;
  if (loading) {
    body = <SectionCard testId="market-loading"><Skeleton className="h-32 w-full" /></SectionCard>;
  } else if (shownMarket) {
    body = (
      <div className="flex flex-col gap-3">
        {/* The "Generated by" badge sits ABOVE the version chips (Ali, 2026-08-03) and follows
            the SELECTED version: vh.selectedModel is the model recorded on the version on
            screen, with the per-function served-model lookup as the pre-044 fallback. It is a
            record, not a control — the model picker lives on the action card. */}
        {!busy && (
          <div className="flex flex-col gap-2">
            <ServedModelBadge model={vh.selectedModel || servedModels?.['generate-market-analysis-detail'] || servedModels?.['generate-market-analysis']} testId="market-served-model" />
            <VersionSwitcher versions={vh.versions} selected={vh.selected} latest={vh.latest} onSelect={vh.select} testId="market-version-switcher" />
          </div>
        )}
        <MarketResult m={shownMarket} />
      </div>
    );
  } else {
    body = <div className="text-muted-foreground text-sm px-2" data-testid="market-empty"><T k="market.empty" /></div>;
  }

  return (
    <div className="flex flex-col gap-6">
      <SectionCard
        title={t('market.title')}
        testId="market-action-card"
        action={
          <div className="flex items-center gap-3 flex-wrap">
            {(modelsLoading || models.length > 0) && (
              <span className="inline-flex items-center gap-2">
                <span className="text-xs font-medium text-muted-foreground">{t('market.modelLabel')}</span>
                <select
                  className="text-xs border border-border rounded-md px-2 py-1.5 bg-card text-foreground"
                  value={selModel}
                  onChange={(e) => setSelModel(e.target.value)}
                  disabled={busy || modelsLoading}
                  title={t('market.modelLabel')}
                  data-testid="market-model-select"
                >
                  {modelsLoading
                    ? <option value="">{t('common.loading')}</option>
                    : <option value="">{t('walletCore.modelDefault')}</option>}
                  {models.map((mo) => (
                    <option key={mo.model_id} value={mo.model_id}>
                      {(mo.display_label || mo.model_id) + (mo.has_key === false ? ` · ${t('settings.modelNoKey')}` : '')}
                    </option>
                  ))}
                </select>
              </span>
            )}
            {/* ONE button (Ali, 2026-08-03): it starts from scratch and honors the model picker;
                with an existing analysis the server gets regenerate=true so the previous run is
                refine-context. Partial runs need no button here — they auto-resume on arrival
                (claims fix, 046), and the failure banner below carries the only manual retry.
                While a run is resumable the button hides so a wrong click cannot fork history. */}
            {armed && !busy && !m && (
              <span className="text-xs text-muted-foreground" data-testid="market-armed-hint">
                {t('market.armedHint')}
              </span>
            )}
            {!(resumable && !busy) && (
              <Button onClick={() => { setArmed(false); run(!!m, selModel || undefined); }} disabled={busy || !hasIdea} data-testid="generate-market-button">
                <BarChart3 className="h-4 w-4 me-2" /> {primaryLabel()}
              </Button>
            )}
          </div>
        }
      >
        {!hasIdea && <p className="text-sm text-muted-foreground"><T k="market.needIdea" /></p>}
      </SectionCard>
      {creditBlocked && !busy && (
        <SectionCard testId="market-credit-card">
          <CreditBanner testId="market-credit-banner" onResume={resume} />
          <div className="mt-3 flex flex-wrap gap-2 text-xs">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-success-soft text-success border border-success-border">
              <CheckCircle2 className="h-3.5 w-3.5" /> {t('credits.doneCount', { count: creditDone })}
            </span>
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-warning-soft text-warning border border-warning-border">
              <AlertTriangle className="h-3.5 w-3.5" /> {t('credits.waitingCount', { count: creditWaiting })}
            </span>
          </div>
        </SectionCard>
      )}
      {interrupted && !busy && !creditBlocked && (
        <div className="border border-warning-border bg-warning-soft rounded-md p-5 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="market-interrupted">
          <AlertTriangle className="h-5 w-5 text-warning shrink-0" />
          <p className="text-sm text-warning flex-1"><T k="market.interrupted" /></p>
          <Button variant="outline" onClick={resume} data-testid="market-retry-button">
            <RefreshCw className="h-4 w-4 me-2" /> {t('market.retry')}
          </Button>
        </div>
      )}
      {resumable && !interrupted && !busy && !creditBlocked && (
        <div className="border border-info-border bg-info-soft rounded-md p-5 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="market-resumable">
          <History className="h-5 w-5 text-info shrink-0" />
          <p className="text-sm text-info flex-1"><T k="market.resumeHint" /></p>
          <Button variant="outline" onClick={resume} data-testid="market-resume-button">
            <RefreshCw className="h-4 w-4 me-2" /> {t('market.resume')}
          </Button>
        </div>
      )}
      <GenerationProgress
        running={busy && !streamingMarket}
        messages={t('progress.market', { returnObjects: true })}
        durationMs={75000}
        testId="market-generation-progress"
      />
      {body}
    </div>
  );
}
